import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams, useSearchParams } from 'react-router-dom'
import { notification } from 'antd'
import useDirectory from 'hooks/useDirectory'
import useMutation from 'hooks/useMutation'
import { Building } from 'models/Building'
import { useRecoilState } from 'recoil'
import {
  buildingModeState,
  listPointState,
  SelectedMode,
  selectedPoint,
  selectedPointDetail,
  selectedSubImageState,
  setAnnotationModeState
} from 'store/buildingStore'
import {
  currentAnnotation,
  currentPovState,
  povPerPointState
} from 'store/krpanoStore'
import useSWR from 'swr'
import { Annotation, AnnotationType, Point } from 'types/building'
import {
  Direction,
  KrpanoInitProps,
  KrpanoOptions,
  Stairway
} from 'types/krpano'
import Utils from 'utils'
import Krpano from 'utils/krpano'
import { KrpanoEngine } from 'utils/KrpanoEngine'
import {
  getPointVersion,
  handlePointVersionError,
  syncPointVersion
} from 'utils/pointVersion'

export default function useKrpano(options: KrpanoOptions) {
  const { id, floorId } = useParams()
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const isDisplay = searchParams.get('is_display')
  const { navigate, getBuiildingType, getDirectory } = useDirectory()

  const [newAnnotationPosition, setNewAnnotationPosition] = useState<any>([])

  const [currentSelectedAnnotation, setCurrentSelectedAnnotation] =
    useRecoilState(currentAnnotation)
  const [currentSelectedPoint, setCurrentSelectedPoint] =
    useRecoilState(selectedPoint)

  const [, setCurrentPov] = useRecoilState(currentPovState)
  const [, setPovPerPoint] = useRecoilState(povPerPointState)
  const [buildingMode, setBuildingMode] = useRecoilState(buildingModeState)
  const [pointDetail, setPointDetail] = useRecoilState(selectedPointDetail)
  const [points, setPoints] = useRecoilState(listPointState)
  const [, setSelectedSetAnnotation] = useRecoilState(setAnnotationModeState)
  const [selectedSubImage, setSelectedSubImage] = useRecoilState(
    selectedSubImageState
  )

  const { mutate: refetchPoint } = useSWR(
    {
      url: 'v1/points',
      params: {
        buildingId: id,
        floorId
      }
    },
    {
      revalidateOnMount: false
    }
  )

  const { data: building } = useSWR<Building>(
    {
      url: `/v1/buildings/by-type?id=${id}&building_type=${getBuiildingType()}`
    },
    {
      revalidateOnMount: false
    }
  )
  // function get all data for create scene

  const { trigger, isMutating } = useMutation(`v1/annotations`)

  const {
    trigger: updateDirectionPosition,
    isMutating: isUpdateDirectionPosition
  } = useMutation(`v1/directions`)

  const { trigger: getAnnotationDetail, isMutating: isAnnotationMutating } =
    useMutation('v1/annotations')

  // handle event of krpano
  const getMousePosition = () => {
    ;(window as any).get_mouse_position = () => {
      const krpano = new KrpanoEngine('full').instance
      if (options?.buildingMode === 'annotation') {
        const h = krpano.get('mouseath')
        const v = krpano.get('mouseatv')
        setNewAnnotationPosition((prev: any) => [...prev, h, v])
        if (!newAnnotationPosition?.length) {
          krpano.call(
            `set(hotspot[temp_circle_style].visible,true);
             set(hotspot[temp_circle_style].ath,${h});
             set(hotspot[temp_circle_style].atv,${v});`
          )
          krpano.call(
            `set(hotspot[temp_polyline_style].visible,true);
             set(hotspot[temp_polyline_style].point[0].ath,${h});
             set(hotspot[temp_polyline_style].point[0].atv,${v});`
          )
        }
        if (newAnnotationPosition?.length === 2) {
          krpano.call(
            `set(hotspot[temp_polyline_style].point[1].ath,${h});
             set(hotspot[temp_polyline_style].point[1].atv,${v});`
          )
          krpano.call(
            `set(hotspot[temp_circle_middle_style].visible,true);
             set(hotspot[temp_circle_middle_style].ath,${h});
             set(hotspot[temp_circle_middle_style].atv,${v});`
          )
          krpano.call(
            `set(hotspot[temp_polyline_style2].visible,true);
             set(hotspot[temp_polyline_style2].point[0].ath,${h});
             set(hotspot[temp_polyline_style2].point[0].atv,${v});`
          )
        }
        if (newAnnotationPosition?.length === 4) {
          krpano.call(
            `set(hotspot[temp_polyline_style2].point[1].ath,${h});
             set(hotspot[temp_polyline_style2].point[1].atv,${v});`
          )
          setCurrentSelectedAnnotation({
            building_id: +(id || 0),
            floor_id: +(floorId || 0),
            point_id: currentSelectedPoint || '',
            head_h: newAnnotationPosition[0],
            head_v: newAnnotationPosition[1],
            mid_h: newAnnotationPosition[2],
            mid_v: newAnnotationPosition[3],
            files: [],
            h,
            v,
            type:
              getBuiildingType() === 20
                ? AnnotationType.FIRE
                : AnnotationType.ARCHITECTURE
          })
          setNewAnnotationPosition([])
        }
      }
      if (
        options?.buildingMode === SelectedMode.TOUR_ANNOTATION &&
        getBuiildingType() === 10
      ) {
        const h = krpano.get('mouseath')
        const v = krpano.get('mouseatv')
        setNewAnnotationPosition((prev: any) => [...prev, h, v])
        setCurrentSelectedAnnotation({
          building_id: +(id || 0),
          floor_id: +(floorId || 0),
          point_id: currentSelectedPoint || '',
          files: [],
          h,
          v,
          type: AnnotationType.IMAGE
        })
        setNewAnnotationPosition([])
      }

      if (
        options?.buildingMode === SelectedMode.TOUR_MEMO_ANNOTATION &&
        getBuiildingType() === 10
      ) {
        const h = krpano.get('mouseath')
        const v = krpano.get('mouseatv')
        setNewAnnotationPosition((prev: any) => [...prev, h, v])
        // Create a small offset for the triangle points
        const offset = 5 // degrees offset for the triangle points
        setCurrentSelectedAnnotation({
          building_id: +(id || 0),
          floor_id: +(floorId || 0),
          point_id: currentSelectedPoint || '',
          files: [],
          h,
          v,
          // set default value for head_h, head_v, mid_h, mid_v for change type of annotation from callout memo to other annotation
          head_h: h - offset, // left point of the triangle
          head_v: v - offset, // top point of the triangle
          mid_h: h + offset, // right point of the triangle
          mid_v: v - offset, // top point of the triangle
          type: AnnotationType.MEMO
        })
        setNewAnnotationPosition([])
      }
    }
  }

  // handle redirect to next point
  const handleRedirect = () => {
    ;(window as any).handle_redirect = (
      item: any,
      clickh: any,
      clickv: any
    ) => {
      if (buildingMode.selectedMode !== 'direction') {
        const ponitSelected = points?.find((point) => point.id === item)
        if (ponitSelected?.image360) {
          Krpano.LoadChecking(() => {
            const krpano = new KrpanoEngine('full').instance
            const h = krpano?.get('view.hlookat')
            const v = krpano?.get('view.vlookat')
            const z = krpano?.get('view.fov')

            const currentPoint =
              pointDetail || points?.find((p) => p.id === currentSelectedPoint)
            const activeImageId = selectedSubImage || currentPoint?.image360?.id
            const hgapOffset = Krpano.GetTransitionHGapOffset(
              currentPoint,
              activeImageId,
              ponitSelected
            )

            krpano.call(`
            hotspot['arrow-%1'].getcenter(${clickh},${clickv});
            if(${clickv} GT -50 AND ${clickv} LT +50 AND 1000 GT 120, set(${clickv},0));
            set3dtransition("sphere", ${clickh}, ${clickv}, ${z}, 0, ${hgapOffset}, 0.0, 0.5, easeoutquad);
            `)

            setCurrentPov({
              h: Number(h),
              v: Number(v),
              z
            })

            selectedSubImage && setSelectedSubImage(undefined)
            setCurrentSelectedPoint(item)
            setBuildingMode((prev) => ({
              ...prev,
              selectedMode: SelectedMode.NONE,
              isClickToDirection: true
            }))
          })
        } else {
          notification.error({
            key: 'redirect',
            message: '画像が設定されていません'
          })
        }
      }
    }
  }

  // handle update direction position
  const handleUpdatePosition = () => {
    ;(window as any).handle_update_direction = (
      link_id: string,
      h: any,
      v: any
    ) => {
      const krpano = new KrpanoEngine('full').instance
      const arrowh = krpano.get(`init_h`)
      const arrowv = krpano.get(`init_v`)

      const dHgap = Krpano.NormalizeHGap(h - arrowh)
      const isChange =
        arrowh !== +h && arrowv !== +v && !isUpdateDirectionPosition

      const activeImageId = selectedSubImage || pointDetail?.image360?.id
      const delta = Krpano.GetSubImageDelta(pointDetail, activeImageId)
      const mainH = Krpano.NormalizeHGap(+h - delta)

      if (isChange) {
        setPointDetail(
          (prev) =>
            prev && {
              ...prev,
              directions: prev.directions
                ? prev.directions.map((item) =>
                    item.id === link_id
                      ? {
                          ...item,
                          h: mainH,
                          v: Krpano.NormalizeHGap(+v),
                          dhgap: dHgap
                        }
                      : item
                  )
                : []
            }
        )
        updateDirectionPosition(
          {
            query: [link_id],
            data: {
              building_id: Number(id),
              floor_id: Number(floorId),
              point_id: currentSelectedPoint,
              h: mainH,
              v: Krpano.NormalizeHGap(+v),
              dhgap: dHgap
            }
          },
          {
            onSuccess: () => {
              refetchPoint().then((data) => {
                setPoints(data.points as Point[])

                setPointDetail(
                  (prev) =>
                    prev && {
                      ...prev,
                      directions: prev.directions
                        ? prev.directions.map((item) =>
                            item.id === link_id
                              ? {
                                  ...item,
                                  h: mainH,
                                  v: Krpano.NormalizeHGap(+v),
                                  dhgap: dHgap
                                }
                              : item
                          )
                        : []
                    }
                )
              })
            }
          }
        )
      }
    }
  }

  // handle selected annotation in area panorama
  const handleClickAnnotation = () => {
    ;(window as any).handle_click_annotation = (annoId: string | undefined) => {
      if (!isAnnotationMutating) {
        const isGeneralAnnotationMode =
          buildingMode.selectedMode === SelectedMode.ANNOTATION
        const isTourAnnotationMode =
          buildingMode.selectedMode === SelectedMode.TOUR_ANNOTATION &&
          getBuiildingType() === 10
        const isTourMemoAnnotationMode =
          buildingMode.selectedMode === SelectedMode.TOUR_MEMO_ANNOTATION &&
          getBuiildingType() === 10

        if (
          !isGeneralAnnotationMode &&
          !isTourAnnotationMode &&
          !isTourMemoAnnotationMode
        ) {
          return
        }

        getAnnotationDetail(
          {
            method: 'get',
            data: {
              building_id: id,
              floor_id: floorId,
              point_id: currentSelectedPoint
            },
            query: [annoId]
          },
          {
            onSuccess: (data) => {
              if (data.set_annotation_id) {
                setSelectedSetAnnotation((prev) => ({
                  ...prev,
                  selectedSetAnnotation: data.set_annotation_id,
                  mode: SelectedMode.EDIT_SET_ANNOTATION,
                  annotationId: annoId
                }))
                return
              }

              const isMovieImageInfoType =
                data.type === AnnotationType.MOVIE ||
                data.type === AnnotationType.IMAGE ||
                data.type === AnnotationType.INFO

              if (isGeneralAnnotationMode && isMovieImageInfoType) {
                return
              }

              if (isTourAnnotationMode && !isMovieImageInfoType) {
                return
              }

              setCurrentSelectedAnnotation({
                ...data,
                building_id: +(id || 0),
                floor_id: +(floorId || 0),
                point_id: currentSelectedPoint
              })
            },
            onError: (error) => {
              if (
                error?.response?.data?.error?.key === 'annotation_not_exists'
              ) {
                setCurrentSelectedAnnotation(undefined)
                const krpano = new KrpanoEngine('full').instance
                krpano.call(`remove_annotation(${annoId})`)
                notification.error({
                  key: 'annotationConflictErr',
                  message: t('translation.deletedAnnotationNotice')
                })
                return
              }
              Utils.handleErrorNavigate(error, navigate)
            }
          }
        )
      }
    }
  }

  const getImageID = () => {
    if (selectedSubImage) {
      return selectedSubImage
    }
    return pointDetail?.image360?.id
  }

  const handleDragEnd = () => {
    ;(window as any).handle_drag_end = (a_id: string, type?: string) => {
      const krpano = new KrpanoEngine('full').instance
      const inith = krpano.get('init_h')
      const initv = krpano.get('init_v')
      const h = krpano.get('drop_h')
      const v = krpano.get('drop_v')
      const isChangeAble = !isMutating && h !== inith && v !== initv

      let annotation: any
      switch (type) {
        case 'head':
          annotation = {
            head_h: h,
            head_v: v
          }
          break
        case 'middle':
          annotation = {
            mid_h: h,
            mid_v: v
          }
          break
        default:
          annotation = {
            h,
            v
          }
          break
      }

      const versionIdToSend = getPointVersion(
        currentSelectedPoint,
        pointDetail,
        points
      )
      isChangeAble &&
        trigger(
          {
            method: 'put',
            headers: { 'X-Point-Version-ID': String(versionIdToSend) },
            data: {
              building_id: Number(id),
              floor_id: Number(floorId),
              point_id: currentSelectedPoint,
              annotation: { ...annotation, id: a_id, imageID: getImageID() }
            }
          },
          {
            onSuccess: (res) => {
              const newVersion =
                res?.data?.pointVersionId ?? res?.pointVersionId
              syncPointVersion(
                newVersion,
                currentSelectedPoint,
                setPointDetail,
                setPoints
              )
              setPointDetail(
                (prev) =>
                  prev && {
                    ...prev,
                    annotations: prev?.annotations.map((item) =>
                      item.id === a_id ? { ...item, ...annotation } : item
                    )
                  }
              )
            },
            onError: (error) => {
              handlePointVersionError(error, t)
            }
          }
        )
    }
  }

  const addPropertiesToKrpano = (
    krpano: any,
    annotations?: Annotation[],
    directions?: Direction[],
    stairways?: Stairway[],
    point?: Point
  ) => {
    annotations &&
      Krpano.AddAnnotation(
        krpano,
        annotations,
        isDisplay,
        getDirectory() === 'tour'
      )
    const targetPoint =
      point || pointDetail || points?.find((p) => p.id === currentSelectedPoint)
    const activeImageId = selectedSubImage || targetPoint?.image360?.id
    const delta = Krpano.GetSubImageDelta(targetPoint, activeImageId)
    directions && Krpano.AddDirectionArrow(krpano, directions, delta)
    stairways && Krpano.AddStairway(krpano, stairways, delta)
  }

  const krpanoOnreadyCallback = async (
    krpano: any,
    krpanoInitProps: KrpanoInitProps,
    annotations?: Annotation[],
    directions?: Direction[],
    stairways?: Stairway[],
    point?: Point
  ) => {
    const { h, v, z, isLocked, previewUrl, multires, imageUrl, tilesUrl } =
      krpanoInitProps

    const varString = `layer[set_first_view_button].visible=true`

    krpano.set('var_string', varString)
    krpano.call(
      `loadpano('/krpano/xml-assets/pano_full.xml', get(var_string), IGNOREKEEP, BLEND(1))`
    )

    const sceneName = Krpano.getScenceNameByFileId(
      currentSelectedPoint,
      points,
      selectedSubImage
    )

    const screenString = Krpano.generateSceneXML({
      sceneName,
      tilesUrl: tilesUrl || undefined,
      multires: multires || undefined,
      s3Path: imageUrl,
      thumbnailUrl: previewUrl || ''
    })

    const sceneStringFixed = screenString.replace(/\r?\n|\r/g, ' ')
    krpano.set('scene_xml', sceneStringFixed)
    krpano.call(`includexmlstring(get(scene_xml), null, null, false)`)

    krpano.call(
      `load_scene(${sceneName},${h},${v},${z}, ${
        buildingMode.selectedMode === 'direction'
      })`
    )

    addPropertiesToKrpano(krpano, annotations, directions, stairways, point)
    Krpano.TogglePointLock(isLocked)
  }

  const embedpano = (
    krpanoInitProps: KrpanoInitProps,
    annotations?: Annotation[],
    directions?: Direction[],
    stairways?: Stairway[],
    point?: Point
  ) => {
    const panoViewer = new KrpanoEngine('full').instance
    const target = document.getElementById('krpano-container')
    const { imageUrl, previewUrl, isLocked, multires, tilesUrl, h, v, z } =
      krpanoInitProps

    if (target) {
      const directionList = (directions || [])
        .map((item) => {
          const order = points.find(
            (point) =>
              point.id !== currentSelectedPoint && item.id.includes(point.id)
          )?.order
          return !order ? item : { ...item, target: order }
        })
        .filter((item) => item.target)

      const stairList = stairways?.map((item) => ({
        ...item,
        nextFloorName: building?.floors.find(
          (floor) => floor.id === +item.nextFloorId
        )?.name,
        areaId: pointDetail?.areaId
      }))

      if (!panoViewer) {
        ;(window as any).embedpano({
          id: 'embedpano-full',
          xml: null,
          target: 'krpano-container',
          html5: 'auto+webgl',
          bgcolor: '#FFFFFF',
          onready: (krpano: any) => {
            krpanoOnreadyCallback(
              krpano,
              krpanoInitProps,
              annotations,
              directionList,
              stairList,
              point
            )
          }
        })
      } else {
        const sceneName = Krpano.getScenceNameByFileId(
          currentSelectedPoint,
          points,
          selectedSubImage
        )

        if (panoViewer.get(`scene[${sceneName}]`) === null) {
          const screenString = Krpano.generateSceneXML({
            sceneName,
            tilesUrl: tilesUrl || undefined,
            multires: multires || undefined,
            s3Path: imageUrl || '',
            thumbnailUrl: previewUrl || ''
          }).replace(/\r?\n|\r/g, ' ')

          panoViewer.set('scene_xml', screenString)
          panoViewer.call(`includexmlstring(get(scene_xml), null, null, false)`)
        }
        panoViewer.call(`load_scene(${sceneName},${h},${v},${z})`)

        Krpano.TogglePointLock(isLocked)

        addPropertiesToKrpano(
          panoViewer,
          annotations,
          directionList,
          stairList,
          point
        )
      }
    }
  }

  // handle event view around
  getMousePosition()
  handleRedirect()
  handleClickAnnotation()
  handleDragEnd()
  handleUpdatePosition()

  useEffect(() => {
    const urlSubimage = searchParams.get('subimage')
    if (urlSubimage) {
      setSelectedSubImage(Number(urlSubimage))
    }
  }, [searchParams, setSelectedSubImage])

  useEffect(() => {
    if (
      buildingMode.selectedMode !== 'annotation' &&
      newAnnotationPosition.length
    ) {
      Krpano.RemoveTempAnnotation()
      setNewAnnotationPosition([])
    }
  }, [buildingMode.selectedMode, newAnnotationPosition.length])

  useEffect(() => {
    ;(window as any).sync_current_pov = (h: number, v: number, z: number) => {
      setCurrentPov({ h, v, z })
      if (pointDetail?.id) {
        setPovPerPoint((prev) => ({
          ...prev,
          [pointDetail.id]: { h, v, z }
        }))
      }
    }
    return () => {
      delete (window as any).sync_current_pov
    }
  }, [setCurrentPov, setPovPerPoint, pointDetail?.id])

  useEffect(() => {
    return () => {
      const isStayingInPreview = localStorage.getItem('previewMode') === 'true'
      if (!isStayingInPreview) {
        Krpano.UnmountPanoFull()
        setBuildingMode((prev) => ({
          ...prev,
          selectedMode: SelectedMode.NONE,
          previewMode: false
        }))
      }
    }
  }, [setBuildingMode])

  return {
    embedpano,
    currentSelectedPoint,
    currentSelectedAnnotation,
    setCurrentSelectedAnnotation
  }
}
